import React, { useMemo } from 'react';

type SparklinePoint = {
  label?: string;
  value: number;
};

type SparklineProps = {
  points: SparklinePoint[];
  positive?: boolean;
  width?: number;
  height?: number;
  className?: string;
};

/** Lightweight SVG sparkline — no chart library required. */
const Sparkline: React.FC<SparklineProps> = ({
  points,
  positive = true,
  width = 120,
  height = 64,
  className = '',
}) => {
  const stroke = positive ? 'var(--primary)' : 'var(--danger)';
  const fill = positive ? 'rgba(0, 205, 21, 0.12)' : 'rgba(220, 53, 69, 0.12)';

  const pathData = useMemo(() => {
    if (!points.length) return null;

    const values = points.map((p) => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const padX = 4;
    const padY = 6;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;

    const coords = values.map((v, i) => {
      const x = padX + (values.length === 1 ? innerW / 2 : (i / (values.length - 1)) * innerW);
      const y = padY + innerH - ((v - min) / range) * innerH;
      return { x, y };
    });

    const line = coords
      .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(2)} ${c.y.toFixed(2)}`)
      .join(' ');

    const area = `${line} L ${coords[coords.length - 1].x.toFixed(2)} ${(height - padY).toFixed(2)} L ${coords[0].x.toFixed(2)} ${(height - padY).toFixed(2)} Z`;

    const last = coords[coords.length - 1];

    return { line, area, last };
  }, [points, width, height]);

  if (!pathData) {
    return (
      <div
        className={`flex items-center justify-center text-xs text-grey_dark ${className}`}
        style={{ width, height }}
      >
        No data
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      className={className}
      role="img"
      aria-label="KPI trend"
    >
      <path d={pathData.area} fill={fill} stroke="none" />
      <path
        d={pathData.line}
        fill="none"
        stroke={stroke}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={pathData.last.x}
        cy={pathData.last.y}
        r={3.5}
        fill={stroke}
      />
    </svg>
  );
};

export default Sparkline;
